import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Brand } from './brands.model';
import { Page } from '../../models/page-wrapper.model';
import { environment } from '../../../environments/environment';
import { PickerRecentCacheService } from '../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class BrandsService {
  private apiUrl = `${environment.restApiUrl}masterdata/brands`;
  private readonly pickerEntityKey = 'brand';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  private handleError(error: HttpErrorResponse) {
    // A client-side or network error occurred.
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${JSON.stringify(error.error)}`
      );
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  getAllBrands(params?: any): Observable<Page<Brand>> {
    return this.http.get<Page<Brand>>(this.apiUrl + '/search', { params: params }).pipe(catchError(this.handleError));
  }

  searchBrands(query: string, page = 0, size = 25): Observable<Page<Brand>> {
    return forkJoin([
      this.searchBrandsByField('brandName', query, page, size),
      this.searchBrandsByField('brandCode', query, page, size),
    ]).pipe(
      map(([namePage, codePage]) => this.mergeBrandPages(namePage, codePage, page, size)),
      catchError(this.handleError)
    );
  }

  getBrandById(id: number): Observable<Brand> {
    return this.http.get<Brand>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  loadRecentBrands(limit = 10): Observable<Brand[]> {
    return from(this.recentPickerCache.loadRecent<Brand>(this.pickerEntityKey, limit));
  }

  getFreshBrandById(id: number | string): Observable<Brand> {
    return this.getBrandById(Number(id));
  }

  rememberBrand(brand: Brand): Observable<void> {
    const record: PickerRecentRecord<Brand> = {
      key: `${this.pickerEntityKey}::${brand.id}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: brand.id,
      label: `${brand.brandName ?? ''}${brand.brandCode ? ` - ${brand.brandCode}` : ''}`.trim(),
      payload: brand,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createBrandPickerAdapter(): EntityDropdownPickerAdapter<Brand> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentBrands(limit),
      search: (query: string, page: number, size: number) => this.searchBrands(query, page, size),
      getFreshById: (id: number | string) => this.getFreshBrandById(id),
      rememberSelection: (brand: Brand) => this.rememberBrand(brand),
      getId: (brand: Brand) => brand.id,
      getLabel: (brand: Brand) => `${brand.brandName ?? ''}${brand.brandCode ? ` - ${brand.brandCode}` : ''}`.trim(),
      getSecondaryLabel: () => '',
      filterBy: 'brandName,brandCode',
    };
  }

  createBrand(brand: Partial<Brand>): Observable<Brand> {
    return this.http.post<Brand>(this.apiUrl, brand).pipe(catchError(this.handleError));
  }

  updateBrand(brand: Partial<Brand>): Observable<Brand> {
    return this.http
      .put<Brand>(`${this.apiUrl}/${brand.id}`, brand)
      .pipe(catchError(this.handleError));
  }

  deleteBrand(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchBrandsByField(
    field: 'brandName' | 'brandCode',
    query: string,
    page: number,
    size: number
  ): Observable<Page<Brand>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<Brand>>(`${this.apiUrl}/search`, { params }).pipe(
      catchError(() => of(this.emptyBrandPage(page, size)))
    );
  }

  private mergeBrandPages(
    namePage: Page<Brand>,
    codePage: Page<Brand>,
    page: number,
    size: number
  ): Page<Brand> {
    const merged = new Map<number, Brand>();
    [...(namePage.content || []), ...(codePage.content || [])].forEach((brand) => {
      if (brand?.id !== undefined && brand?.id !== null) {
        merged.set(brand.id, brand);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);
    return {
      ...namePage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptyBrandPage(page: number, size: number): Page<Brand> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }
}
